import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
} from '@mui/material';
import { ContentCopy, Close } from '@mui/icons-material';
import { toast } from 'react-toastify';
import PixRoundedIcon from '@mui/icons-material/PixRounded';
import DocumentScannerRoundedIcon from '@mui/icons-material/DocumentScannerRounded';
import { normalizeCNPJ } from '../utils';
import { useTranslation } from '../hooks/useTranslation';
import TheFinalCountdown from './TheFinalCountDown';
import { usePayment } from '../context/PaymentContext';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

const green = '#83D84D';

const PaymentModal = ({ type, data, onClose }) => {
  const { t } = useTranslation();
  const { paymentStatus, setPaymentStatus } = usePayment();
  const navigate = useNavigate();

  const pixIsPaid = paymentStatus === 'PAID';

  useEffect(() => {
    return () => {
      setPaymentStatus('');
    };
  }, [setPaymentStatus]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado para área de transferência!');
  };

  const downloadPDF = () => {
    if (data?.payment_data?.payment_url) {
      window.open(data.payment_data.payment_url, '_blank');
    }
  };

  const handleReturn = () => {
    onClose();
    navigate('/app/redscore');
  };

  return (
    <>
      {!pixIsPaid ? (
        <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
          {/* Header */}
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {type === 'pix' ? (
                  <PixRoundedIcon sx={{ color: green, width: '32px', height: '32px' }} />
                ) : (
                  <DocumentScannerRoundedIcon
                    sx={{ color: green, width: '32px', height: '32px' }}
                  />
                )}
                <Typography fontWeight={600} color="#939598">
                  {type === 'pix' ? t('payment.payWithPix') : t('payment.payWithBill')}
                </Typography>
              </Box>
              <IconButton onClick={onClose} sx={{ color: '#2C2C2C' }}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent>
            <Box>
              {/* PIX */}
              {type === 'pix' && data?.payment_data && (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexDirection: 'column',
                    }}
                  >
                    <TheFinalCountdown time={data?.created_at} />

                    <Typography fontWeight={600} mb={2}>
                      {t('payment.awaitingPayment')}
                    </Typography>

                    {data.payment_data.qrCodeImage && (
                      <Box
                        mb={2}
                        sx={{
                          border: '1px solid #939598',
                          padding: '8px',
                          borderRadius: '12PX',
                        }}
                      >
                        <img
                          src={data.payment_data.qrCodeImage.base64}
                          alt="QR Code PIX"
                          style={{ width: 200 }}
                        />
                      </Box>
                    )}

                    <Typography variant="body2" mb={1}>
                      {t('payment.scanQrCode')}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    value={data.payment_data.barcode || data.payment_data.textPayment}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <IconButton
                          onClick={() =>
                            copyToClipboard(
                              data.payment_data.barcode || data.payment_data.textPayment
                            )
                          }
                        >
                          <ContentCopy sx={{ color: green }} />
                        </IconButton>
                      ),
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        border: `1px solid ${green}`,
                      },
                    }}
                  />

                  <Box
                    sx={{
                      backgroundColor: '#F2F3F5',
                      borderRadius: 2,
                      p: 2,
                      textAlign: 'left',
                    }}
                  >
                    <Typography fontWeight={600} mb={1}>
                      {t('payment.howToPay')}?
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '14px' }}>
                      {t('payment.openDigitalBank')} <b>{t('payment.chooseToPayWith')} pix</b>
                      <br />
                      {t('payment.selectOption')} <b>{t('payment.payWithQrCode')}</b>{' '}
                      {t('payment.scanCodeAbove').toLowerCase()}{' '}
                      <b>{t('payment.copyTheCode').toLowerCase()}</b>{' '}
                      {t('payment.and').toLowerCase()} {t('payment.selectOption').toLowerCase()}{' '}
                      <b>Pix {t('payment.copyAndPaste')}</b>
                      <br />
                      {t('payment.confirmInformation')}
                    </Typography>
                  </Box>
                </>
              )}

              {/* BOLETO */}
              {type === 'boleto' && data?.payment_data && (
                <>
                  <Box
                    sx={{
                      border: '1px solid #E0E0E0',
                      borderRadius: 2,
                      p: 4,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}
                    >
                      <Typography fontWeight={600} mb={1}>
                        {t('payment.paymentDetails')}
                      </Typography>

                      <Box
                        sx={{
                          padding: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          border: '1px solid #D9D9D9',
                          gap: '4px',
                        }}
                      >
                        <Typography variant="body2">Beneficiário:</Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Typography variant="body2">{data?.company?.name}</Typography>
                          <Typography variant="body2">
                            <b>CNPJ:</b> {normalizeCNPJ(data?.company?.taxvar)}
                          </Typography>
                        </Box>
                      </Box>

                      <Typography
                        fontWeight={600}
                        sx={{
                          textAlign: 'center',
                          fontSize: { xs: '14px', md: '18px' },
                          whiteSpace: { xs: 'normal', md: 'nowrap' },
                          wordBreak: 'break-word',
                        }}
                      >
                        {data.payment_data.barcode}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography mb={1} mt={2} sx={{ textAlign: 'center' }}>
                    {t('payment.useTheBarCode')}
                  </Typography>

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => copyToClipboard(data.payment_data.barcode)}
                    sx={{
                      backgroundColor: green,
                      mb: 1,
                      fontWeight: 600,
                      display: 'flex',
                      justifyContent: 'center',
                      position: 'relative',
                      paddingY: 1,
                      '&:hover': {
                        backgroundColor: '#000000',
                      },
                    }}
                  >
                    <p>{t('payment.copyTheLine')}</p>

                    <ContentCopy
                      sx={{
                        color: '#fff',
                        position: 'absolute',
                        right: 16,
                      }}
                    />
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={downloadPDF}
                    sx={{
                      color: green,
                      borderColor: green,
                      fontWeight: 600,
                      mb: 2,
                      paddingY: 1,
                    }}
                  >
                    {t('payment.viewBill')}
                  </Button>

                  <Box
                    sx={{
                      backgroundColor: '#F2F3F5',
                      borderRadius: 2,
                      p: 2,
                      textAlign: 'left',
                    }}
                  >
                    <Typography fontWeight={600} mb={1}>
                      {t('payment.howToPay')}?
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '14px', lineHeight: '22px' }}>
                      {t('payment.openDigitalBank')}{' '}
                      <b>
                        {t('payment.chooseToPayWith')} {t('payment.bill').toLowerCase()}
                      </b>
                      <br />
                      {t('payment.selectOption')} <b>{t('payment.enterBarCode').toLowerCase()}</b>{' '}
                      {t('payment.and')} <b>{t('payment.copyAndPaste')}</b> {t('payment.lineTyped')}
                      <br />
                      {t('payment.confirmInformation')}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </DialogContent>
        </Dialog>
      ) : (
        <Dialog open={pixIsPaid} maxWidth="sm" fullWidth>
          <DialogContent>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              py={6}
              gap={2}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img src="/success.svg" alt="Sucesso" style={{ width: 100, height: 100 }} />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography fontWeight={700} fontSize="20px">
                  {t('payment.paymentSuccess')}
                </Typography>

                <Typography color="#6F6F6F">{t('payment.thanksForPurchase')}</Typography>
              </Box>

              <Button
                variant="contained"
                onClick={handleReturn}
                sx={{
                  mt: 2,
                  color: '#2C2C2C',
                  backgroundColor: green,
                  fontWeight: 700,
                  px: 4,
                  '&:hover': {
                    backgroundColor: '#45a049',
                  },
                }}
              >
                {t('payment.return').toUpperCase()}
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default PaymentModal;
